import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {FooterBar} from 'shells/modal/footer-bar';
import {MarketingHeader} from 'shells/typography/marketing-header';
import {ModalBody} from 'shells/typography/modal-body';

import type {GetMcfxForms_mcfxForms_edges_node_wfForm_publishedVersion_fields as Field} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../../core/nutshell-fields/types';

import {ConnectedFieldsSection} from './connected-fields-section';
import {getConnectedEntitiesList} from './helpers';
import {IgnoredFieldsSection} from './ignored-fields-section';
import {UnconnectedFieldsSection} from './unconnected-fields-section';

import styles from './activate-form-modal.module.css';

type Props = {
    fields: Field[];
    formSchema: FormFieldSchema;
    onClose: () => void;
    onActivateForm: () => Promise<any>;
};

export function ActivateFormModalContent(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const ignoredFields = props.fields.filter((field) => field.isIgnored);
    const connectedFields = props.fields.filter((field) => !field.isIgnored && !field.needsMapping);
    const unconnectedFields = props.fields.filter((field) => field.needsMapping);

    const entitiesString = getConnectedEntitiesList(connectedFields);

    const header = connectedFields.length ? 'Activate form' : 'Connect a field to activate form';

    const subtitle = connectedFields.length
        ? `Once activated, this form will begin syncing submission data to new or existing ${entitiesString}`
        : 'To activate this form, connect one or more fields to a person, company, or lead';

    const onActivateClick = () => {
        setIsSaving(true);
        props.onActivateForm();
    };

    return (
        <>
            <div className={styles.container}>
                <MarketingHeader>{header}</MarketingHeader>
                <ModalBody>{subtitle}</ModalBody>
                <div className='flex justify-sb'>
                    {unconnectedFields.length ? (
                        <UnconnectedFieldsSection fieldCount={unconnectedFields.length} />
                    ) : undefined}
                    {ignoredFields.length ? (
                        <IgnoredFieldsSection fieldCount={ignoredFields.length} />
                    ) : undefined}
                </div>
                {connectedFields.length ? (
                    <ConnectedFieldsSection
                        connectedFields={connectedFields}
                        entitiesString={entitiesString}
                        formSchema={props.formSchema}
                    />
                ) : undefined}
            </div>
            <FooterBar
                left={
                    <Button variant='text' size='big' onClick={props.onClose}>
                        Cancel
                    </Button>
                }
                right={
                    <SaveButton
                        variant='primary'
                        size='big'
                        // @ts-expect-error
                        onClick={onActivateClick}
                        disabled={connectedFields.length === 0}
                        isSaving={isSaving}
                        buttonText='Activate'
                        savingText='Activating…'
                        successText='Success!'
                    />
                }
            />
        </>
    );
}
